import JSZip from "jszip";
import type { RdPercentageSnapshot } from "../store/superAdmin/dataExtractSlice";

const xmlEscape = (value: string) =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const colLetter = (index: number) => {
  let n = index + 1;
  let out = "";
  while (n > 0) {
    const rem = (n - 1) % 26;
    out = String.fromCharCode(65 + rem) + out;
    n = Math.floor((n - 1) / 26);
  }
  return out;
};

const sheetName = (label: string, used: Set<string>) => {
  const base = label.replace(/[\\/?*[\]]/g, " ").trim().slice(0, 28) || "Sheet";
  let name = base;
  let i = 2;
  while (used.has(name.toLowerCase())) {
    name = `${base.slice(0, 26)} ${i}`;
    i += 1;
  }
  used.add(name.toLowerCase());
  return name;
};

const inlineCell = (ref: string, value: string) =>
  `<c r="${ref}" t="inlineStr"><is><t>${xmlEscape(value)}</t></is></c>`;

const numCell = (ref: string, value: number | null | undefined) =>
  `<c r="${ref}"><v>${Number(value || 0)}</v></c>`;

const rowXml = (r: number, cells: string) => `<row r="${r}">${cells}</row>`;

const worksheetXml = (rows: string, drawingRid?: string) => `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheetData>${rows}</sheetData>
  ${drawingRid ? `<drawing r:id="${drawingRid}"/>` : ""}
</worksheet>`;

const stylesXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <fonts count="1"><font><sz val="11"/><color theme="1"/><name val="Calibri"/><family val="2"/></font></fonts>
  <fills count="2">
    <fill><patternFill patternType="none"/></fill>
    <fill><patternFill patternType="gray125"/></fill>
  </fills>
  <borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>
  <cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
  <cellXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/></cellXfs>
</styleSheet>`;

const relsXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`;

type ChartSeries = { name: string; values: number[] };

const strCacheXml = (values: string[]) =>
  `<c:strCache><c:ptCount val="${values.length}"/>${values
    .map((value, i) => `<c:pt idx="${i}"><c:v>${xmlEscape(value)}</c:v></c:pt>`)
    .join("")}</c:strCache>`;

const numCacheXml = (values: number[]) =>
  `<c:numCache><c:formatCode>General</c:formatCode><c:ptCount val="${values.length}"/>${values
    .map((value, i) => `<c:pt idx="${i}"><c:v>${Number(value || 0)}</c:v></c:pt>`)
    .join("")}</c:numCache>`;

const columnChartXml = (
  title: string,
  sheet: string,
  categories: string[],
  series: ChartSeries[],
  catRange: string,
  valueCols: string[],
) => {
  const quotedSheet = `'${sheet.replace(/'/g, "''")}'`;
  const seriesXml = series
    .map(
      (item, index) => `
      <c:ser>
        <c:idx val="${index}"/>
        <c:order val="${index}"/>
        <c:tx><c:v>${xmlEscape(item.name)}</c:v></c:tx>
        <c:cat>
          <c:strRef>
            <c:f>${quotedSheet}!${catRange}</c:f>
            ${strCacheXml(categories)}
          </c:strRef>
        </c:cat>
        <c:val>
          <c:numRef>
            <c:f>${quotedSheet}!${valueCols[index]}</c:f>
            ${numCacheXml(item.values)}
          </c:numRef>
        </c:val>
      </c:ser>`,
    )
    .join("");

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<c:chartSpace xmlns:c="http://schemas.openxmlformats.org/drawingml/2006/chart" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <c:chart>
    <c:title>
      <c:tx><c:rich><a:bodyPr/><a:lstStyle/><a:p><a:pPr><a:defRPr sz="1200"/></a:pPr><a:r><a:rPr lang="en-US" sz="1200"/><a:t>${xmlEscape(title)}</a:t></a:r></a:p></c:rich></c:tx>
      <c:overlay val="0"/>
    </c:title>
    <c:plotArea>
      <c:layout/>
      <c:barChart>
        <c:barDir val="col"/>
        <c:grouping val="clustered"/>
        <c:overlap val="0"/>
        ${seriesXml}
        <c:axId val="1"/>
        <c:axId val="2"/>
      </c:barChart>
      <c:catAx>
        <c:axId val="1"/>
        <c:scaling><c:orientation val="minMax"/></c:scaling>
        <c:delete val="0"/>
        <c:axPos val="b"/>
        <c:tickLblPos val="nextTo"/>
        <c:crossAx val="2"/>
        <c:crosses val="autoZero"/>
        <c:auto val="1"/>
        <c:lblAlgn val="ctr"/>
        <c:lblOffset val="100"/>
      </c:catAx>
      <c:valAx>
        <c:axId val="2"/>
        <c:scaling><c:orientation val="minMax"/></c:scaling>
        <c:delete val="0"/>
        <c:axPos val="l"/>
        <c:majorGridlines/>
        <c:numFmt formatCode="General" sourceLinked="1"/>
        <c:tickLblPos val="nextTo"/>
        <c:crossAx val="1"/>
        <c:crosses val="autoZero"/>
      </c:valAx>
    </c:plotArea>
    <c:legend>
      <c:legendPos val="b"/>
      <c:overlay val="0"/>
    </c:legend>
    <c:plotVisOnly val="1"/>
  </c:chart>
</c:chartSpace>`;
};

const drawingXml = (chartCount: number) => {
  const anchors = Array.from({ length: chartCount }, (_, i) => {
    const startRow = 10 + i * 18;
    const endRow = startRow + 16;
    const rid = i + 1;
    return `
  <xdr:twoCellAnchor>
    <xdr:from><xdr:col>0</xdr:col><xdr:colOff>0</xdr:colOff><xdr:row>${startRow}</xdr:row><xdr:rowOff>0</xdr:rowOff></xdr:from>
    <xdr:to><xdr:col>10</xdr:col><xdr:colOff>0</xdr:colOff><xdr:row>${endRow}</xdr:row><xdr:rowOff>0</xdr:rowOff></xdr:to>
    <xdr:graphicFrame macro="">
      <xdr:nvGraphicFramePr>
        <xdr:cNvPr id="${rid + 1}" name="Chart ${rid}"/>
        <xdr:cNvGraphicFramePr><a:graphicFrameLocks noGrp="1"/></xdr:cNvGraphicFramePr>
      </xdr:nvGraphicFramePr>
      <xdr:xfrm><a:off x="0" y="0"/><a:ext cx="0" cy="0"/></xdr:xfrm>
      <a:graphic>
        <a:graphicData uri="http://schemas.openxmlformats.org/drawingml/2006/chart">
          <c:chart xmlns:c="http://schemas.openxmlformats.org/drawingml/2006/chart" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" r:id="rId${rid}"/>
        </a:graphicData>
      </a:graphic>
    </xdr:graphicFrame>
    <xdr:clientData/>
  </xdr:twoCellAnchor>`;
  }).join("");

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<xdr:wsDr xmlns:xdr="http://schemas.openxmlformats.org/drawingml/2006/spreadsheetDrawing" xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main">
${anchors}
</xdr:wsDr>`;
};

const snapshotSheetRows = (snap: RdPercentageSnapshot) => {
  const labs = snap.labs || [];
  const lines: string[] = [
    rowXml(1, `${inlineCell("A1", snap.label)}`),
    rowXml(2, `${inlineCell("A2", "From")}${inlineCell("B2", snap.fromDate)}`),
    rowXml(3, `${inlineCell("A3", "To")}${inlineCell("B3", snap.toDate)}`),
    rowXml(4, `${inlineCell("A4", "Window")}${inlineCell("B4", `${snap.window?.from || ""} → ${snap.window?.to || ""}`)}`),
    rowXml(6, `${inlineCell("A6", "Metric")}${inlineCell("B6", "Value")}`),
    rowXml(7, `${inlineCell("A7", "Live cases")}${numCell("B7", snap.counts.live)}`),
    rowXml(8, `${inlineCell("A8", "Total redesigns")}${numCell("B8", snap.counts.total_redesigns)}`),
    rowXml(9, `${inlineCell("A9", "Internal redesigns")}${numCell("B9", snap.counts.internal)}`),
    rowXml(10, `${inlineCell("A10", "Client redesigns")}${numCell("B10", snap.counts.client)}`),
    rowXml(11, `${inlineCell("A11", "Total RD %")}${numCell("B11", snap.percentages.total_rd_of_live)}`),
    rowXml(12, `${inlineCell("A12", "Internal RD %")}${numCell("B12", snap.percentages.internal_of_live)}`),
    rowXml(13, `${inlineCell("A13", "Client RD %")}${numCell("B13", snap.percentages.client_of_live)}`),
    rowXml(15, `${inlineCell("A15", "Lab name")}${inlineCell("B15", "Total cases")}${inlineCell("C15", "Total redesign")}${inlineCell("D15", "RD %")}`),
  ];
  labs.forEach((lab, index) => {
    const r = 16 + index;
    lines.push(
      rowXml(
        r,
        `${inlineCell(`A${r}`, lab.lab)}${numCell(`B${r}`, lab.total_cases)}${numCell(`C${r}`, lab.total_redesigns)}${numCell(`D${r}`, lab.rd_pct)}`,
      ),
    );
  });
  const totalRow = 16 + labs.length;
  const totalCases = labs.reduce((sum, lab) => sum + Number(lab.total_cases || 0), 0);
  const totalRd = labs.reduce((sum, lab) => sum + Number(lab.total_redesigns || 0), 0);
  const totalPct = totalCases ? Math.round((totalRd / totalCases) * 1000) / 10 : 0;
  lines.push(
    rowXml(
      totalRow,
      `${inlineCell(`A${totalRow}`, "Total")}${numCell(`B${totalRow}`, totalCases)}${numCell(`C${totalRow}`, totalRd)}${numCell(`D${totalRow}`, totalPct)}`,
    ),
  );
  return lines.join("");
};

export const downloadLiveRedesignExcel = async (
  snapshots: RdPercentageSnapshot[],
  fileName: string,
) => {
  if (!snapshots.length) return;
  const usedNames = new Set<string>();
  const summaryName = sheetName("Summary", usedNames);
  const sheetMeta = snapshots.map((snap) => ({
    snap,
    name: sheetName(snap.label, usedNames),
  }));

  const headers = [
    "Period",
    "Live cases",
    "Total redesigns",
    "Internal redesigns",
    "Client redesigns",
    "Total RD %",
    "Internal RD %",
    "Client RD %",
    "From",
    "To",
  ];
  const summaryRows = [
    rowXml(
      1,
      headers.map((header, i) => inlineCell(`${colLetter(i)}1`, header)).join(""),
    ),
    ...snapshots.map((snap, index) => {
      const r = index + 2;
      const values = [
        snap.label,
        snap.counts.live,
        snap.counts.total_redesigns,
        snap.counts.internal,
        snap.counts.client,
        snap.percentages.total_rd_of_live,
        snap.percentages.internal_of_live,
        snap.percentages.client_of_live,
        snap.fromDate,
        snap.toDate,
      ];
      return rowXml(
        r,
        values
          .map((value, i) =>
            i === 0 || i >= 8
              ? inlineCell(`${colLetter(i)}${r}`, String(value ?? ""))
              : numCell(`${colLetter(i)}${r}`, Number(value || 0)),
          )
          .join(""),
      );
    }),
  ];

  const lastRow = snapshots.length + 1;
  const categories = snapshots.map((snap) => snap.label);
  const pctSeries: ChartSeries[] = [
    {
      name: "Total RD %",
      values: snapshots.map((snap) => Number(snap.percentages.total_rd_of_live || 0)),
    },
    {
      name: "Internal RD %",
      values: snapshots.map((snap) => Number(snap.percentages.internal_of_live || 0)),
    },
    {
      name: "Client RD %",
      values: snapshots.map((snap) => Number(snap.percentages.client_of_live || 0)),
    },
  ];
  const countSeries: ChartSeries[] = [
    { name: "Live cases", values: snapshots.map((snap) => Number(snap.counts.live || 0)) },
    {
      name: "Total redesigns",
      values: snapshots.map((snap) => Number(snap.counts.total_redesigns || 0)),
    },
    {
      name: "Internal redesigns",
      values: snapshots.map((snap) => Number(snap.counts.internal || 0)),
    },
    {
      name: "Client redesigns",
      values: snapshots.map((snap) => Number(snap.counts.client || 0)),
    },
  ];

  const zip = new JSZip();
  const sheetFiles = [
    {
      path: "xl/worksheets/sheet1.xml",
      xml: worksheetXml(summaryRows.join(""), "rId1"),
      name: summaryName,
    },
    ...sheetMeta.map((item, index) => ({
      path: `xl/worksheets/sheet${index + 2}.xml`,
      xml: worksheetXml(snapshotSheetRows(item.snap)),
      name: item.name,
    })),
  ];

  const workbookSheets = sheetFiles
    .map(
      (sheet, index) =>
        `<sheet name="${xmlEscape(sheet.name)}" sheetId="${index + 1}" r:id="rId${index + 1}"/>`,
    )
    .join("");
  const workbookXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheets>${workbookSheets}</sheets>
</workbook>`;
  const workbookRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId0" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
  ${sheetFiles
    .map(
      (sheet, index) =>
        `<Relationship Id="rId${index + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${index + 1}.xml"/>`,
    )
    .join("")}
</Relationships>`;

  const overrides = [
    '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>',
    '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>',
    ...sheetFiles.map(
      (sheet, index) =>
        `<Override PartName="/xl/worksheets/sheet${index + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`,
    ),
    '<Override PartName="/xl/drawings/drawing1.xml" ContentType="application/vnd.openxmlformats-officedocument.drawing+xml"/>',
    '<Override PartName="/xl/charts/chart1.xml" ContentType="application/vnd.openxmlformats-officedocument.drawingml.chart+xml"/>',
    '<Override PartName="/xl/charts/chart2.xml" ContentType="application/vnd.openxmlformats-officedocument.drawingml.chart+xml"/>',
  ].join("");

  const contentTypes = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  ${overrides}
</Types>`;

  zip.file("[Content_Types].xml", contentTypes);
  zip.file("_rels/.rels", relsXml);
  zip.file("xl/workbook.xml", workbookXml);
  zip.file("xl/_rels/workbook.xml.rels", workbookRels);
  zip.file("xl/styles.xml", stylesXml);
  sheetFiles.forEach((sheet) => zip.file(sheet.path, sheet.xml));
  zip.file(
    "xl/worksheets/_rels/sheet1.xml.rels",
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/drawing" Target="../drawings/drawing1.xml"/>
</Relationships>`,
  );
  zip.file("xl/drawings/drawing1.xml", drawingXml(2));
  zip.file(
    "xl/drawings/_rels/drawing1.xml.rels",
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/chart" Target="../charts/chart1.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/chart" Target="../charts/chart2.xml"/>
</Relationships>`,
  );
  zip.file(
    "xl/charts/chart1.xml",
    columnChartXml(
      "Redesign %",
      summaryName,
      categories,
      pctSeries,
      `$A$2:$A$${lastRow}`,
      [`$F$2:$F$${lastRow}`, `$G$2:$G$${lastRow}`, `$H$2:$H$${lastRow}`],
    ),
  );
  zip.file(
    "xl/charts/chart2.xml",
    columnChartXml(
      "Live vs redesign counts",
      summaryName,
      categories,
      countSeries,
      `$A$2:$A$${lastRow}`,
      [`$B$2:$B$${lastRow}`, `$C$2:$C$${lastRow}`, `$D$2:$D$${lastRow}`, `$E$2:$E$${lastRow}`],
    ),
  );

  const blob = await zip.generateAsync({
    type: "blob",
    mimeType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName.endsWith(".xlsx") ? fileName : `${fileName}.xlsx`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
